// A staged template's still card (live's shared `StillCard`; Podcast Ad's and Street Interview
// Ad's cast stages): the picture once drawn, the failed notice, the loader, and ↻; Pixar Talking
// Ad's refused-take notice under a frame, with its action (live's `pixar-still-card`).
import { ActionIcon, Button, Loader, Text, Tooltip } from "@mantine/core";
import { ImageOff, RefreshCw, Tag } from "lucide-react";

import type { StillStatus } from "../../../api/generated/model";
import classes from "./podcast.module.css";

/** Live's words (`still-card/constants.ts`). */
const REGENERATE = "Regenerate";
export const FIX_LABEL = "Fix label";

/**
 * Live's still card (`StillCard`): the picture once drawn (a click previews it), the failed
 * notice with its words, live's loader while it or a still it is drawn from draws, or nothing
 * before. Settled (drawn or failed), ↻ draws it again; Podcast's drawn master also offers Fix
 * label.
 */
export interface StillCardProps {
  /** Live's label under the card (`STILL_LABELS`). */
  label: string;
  still: { status: StillStatus; url: string | null; problem: string | null };
  waiting: boolean;
  busy: boolean;
  onRegenerate: () => void;
  /** Podcast Ad's master's Fix label, once it is drawn (live's `canFixLabel`); none else. */
  onFixLabel: (() => void) | null;
  onPreview: () => void;
  /** A 16:9 still, at the row's height (live's `isWide`: Pixar Talking Ad's character sheet). */
  wide?: boolean;
  /** Live's notice under a drawn frame whose clip the video model refused (Pixar Talking Ad). */
  flag?: string | null;
  /** The notice's action (live's "Build without end card"), beside it. */
  action?: { label: string; disabled: boolean; onClick: () => void } | null;
}

export function StillCard(props: StillCardProps) {
  const { label, still, waiting, busy, onRegenerate, onFixLabel, onPreview, wide = false } = props;
  const { flag = null, action = null } = props;
  const done = still.status === "done";
  const settled = done || still.status === "failed";
  let content = null;
  if (done && still.url !== null) {
    content = (
      <button type="button" className={classes.stillPicture} aria-label={`Preview the ${label}`} onClick={onPreview}>
        <img src={still.url} alt="" />
      </button>
    );
  } else if (still.status === "failed") {
    content = (
      <span className={classes.stillFailed} role="alert">
        <ImageOff size={18} aria-hidden />
        <span>{still.problem ?? "Rendering the image failed."}</span>
      </span>
    );
  } else if (waiting) {
    content = <Loader size="sm" aria-label={`Drawing the ${label}`} />;
  }
  return (
    <fieldset className={classes.still} data-wide={wide || undefined} aria-label={label}>
      <div className={classes.stillFrame}>
        {content}
        {settled ? (
          <div className={classes.stillActions}>
            <Tooltip label={REGENERATE} withArrow>
              <ActionIcon
                variant="default"
                size="sm"
                className={classes.stillAction}
                aria-label={`${REGENERATE} the ${label}`}
                disabled={busy}
                onClick={onRegenerate}
              >
                <RefreshCw size={14} aria-hidden />
              </ActionIcon>
            </Tooltip>
            {onFixLabel !== null && done ? (
              <Tooltip label={FIX_LABEL} withArrow>
                <ActionIcon
                  variant="default"
                  size="sm"
                  className={classes.stillAction}
                  aria-label={FIX_LABEL}
                  disabled={busy}
                  onClick={onFixLabel}
                >
                  <Tag size={14} aria-hidden />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>
      <Text className={classes.stillLabel}>{label}</Text>
      {flag === null ? null : (
        <Text className={classes.fieldError} role="alert">
          {flag}
        </Text>
      )}
      {action === null ? null : (
        <Button variant="subtle" size="compact-xs" disabled={action.disabled} onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </fieldset>
  );
}
